import { useContext } from 'react'
import { IntakeContext } from 'context/IntakeContext'
import dashboardData from 'layouts/authenticated/pages/dashboard/data/dashboardData'
import { selectData } from 'layouts/onboarding'
import { useDashboardCalculations } from './useDashboardCalculations'

import {
  format,
  addYears,
  addDays,
  addMonths,
  max,
  min,
  differenceInMonths,
  differenceInYears,
  isAfter,
} from 'date-fns'
import { LogoDev } from '@mui/icons-material'

function getRankedFEGLICombos({ targetPremium, maxResults = 10 }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { intakeState } = useContext(IntakeContext)
  const age = Math.floor(
    differenceInMonths(new Date(), new Date(intakeState.dateOfBirth)) / 12
  )
  const ageBracket = selectData.fegli[age]
  const isPostal = intakeState?.agency === 'u.s._postal_service_(usps)'
  const baseSalary = intakeState?.baseSalary
  const baseInThousands = Math.ceil(baseSalary / 1000)

  const basicPremium = isPostal ? 0 : 0.3467 * (baseInThousands + 2)
  const optionAPremium = ageBracket.optionA

  const combos = []

  for (let includeA = 0; includeA <= 1; includeA++) {
    for (let bMult = 0; bMult <= 5; bMult++) {
      for (let cMult = 0; cMult <= 5; cMult++) {
        const bPremium = ageBracket.optionB * (bMult * baseInThousands)
        const cPremium = ageBracket.optionC * cMult

        const totalPremium =
          basicPremium + (includeA ? optionAPremium : 0) + bPremium + cPremium

        combos.push({
          total: totalPremium,
          difference: Math.abs(totalPremium - targetPremium),
          optionA: !!includeA,
          optionBMultiples: bMult,
          optionCMultiples: cMult,
        })
      }
    }
  }

  // Sort by closeness to target
  return combos.sort((a, b) => a.difference - b.difference).slice(0, maxResults)
}

export const useFegliCalculations = (fegliOptionAgeRange) => {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { intakeState } = useContext(IntakeContext)
    const {
      presentValueSurvivorBenefit,
      presentValueClientIncome,
      atRetirementGrossPensionToday,
      atRetirementGrossPension,
      survivorBenefitCost,
    } = useDashboardCalculations()

    const dob = new Date(intakeState?.dateOfBirth)
    const scd = new Date(intakeState?.dateOfHire)
    const age65Date = new Date(
      dob.getFullYear() + 65,
      dob.getMonth(),
      dob.getDate()
    )

    //Date Values
    const retirementDate =
      intakeState?.dateOfPlannedRetirement === null
        ? new Date()
        : new Date(intakeState.dateOfPlannedRetirement)
    const plannedRetirementDate = format(retirementDate, 'MMMM do, yyyy')
    const plannedRetirementMonth = format(retirementDate, 'MMMM')
    const plannedRetirementDay = format(addDays(retirementDate, 1), 'do')
    const plannedRetirementYear = format(retirementDate, 'yyyy')
    const yearsUntilRetirement = intakeState?.dateOfPlannedRetirement
      ? differenceInYears(
          new Date(intakeState.dateOfPlannedRetirement),
          new Date()
        )
      : 0

    //Personal Details
    const personalDetailsCurrentAge =
      differenceInMonths(new Date(), new Date(intakeState.dateOfBirth)) / 12
    const personalDetailsPlannedRetirementAge = Math.floor(
      differenceInMonths(
        new Date(intakeState.dateOfPlannedRetirement),
        new Date(intakeState.dateOfBirth)
      ) / 12
    )

    const fegliLookupAgeValue = fegliOptionAgeRange
      ? fegliOptionAgeRange
      : personalDetailsPlannedRetirementAge
    const fegliLookupDateValue = addYears(dob, fegliLookupAgeValue)

    //FEGLI Calcs
    const bedb =
      intakeState?.familyMaritalStatus === 'married' &&
      intakeState?.employmentStatus === 'employed'
        ? intakeState?.baseSalary / 2 + 41527.75
        : ''
    const basicLifeCoverage =
      (Math.round(personalDetailsCurrentAge) < 45
        ? 1 + (45 - Math.max(Math.round(personalDetailsCurrentAge), 35)) / 10
        : 1) *
      ((Math.ceil(intakeState?.baseSalary / 1000) + 2) * 1000)

    // New Basic Life Calculations
    const reductionStartDate = max([retirementDate, age65Date])
    const monthsSinceReductionStart = isAfter(
      fegliLookupDateValue,
      reductionStartDate
    )
      ? differenceInMonths(fegliLookupDateValue, reductionStartDate)
      : 0
    const reductionType = intakeState?.fegliExtraBLReduction || 'noreduction'
    let reductionMultiplier = 1
    let ratePerThousand = 0.3467
    let maxReduction = 0
    let monthlyReduction = 0
    const isPost65 = fegliLookupAgeValue >= 65
    switch (reductionType) {
      case '75%': // 75% Reduction
        maxReduction = 0.75
        monthlyReduction = 0.02
        ratePerThousand = isPost65 ? 0 : 0.3467
        break
      case '50%': // 50% Reduction
        maxReduction = 0.5
        monthlyReduction = 0.01
        ratePerThousand = isPost65 ? 0.75 : 1.0967
        break
      case 'noreduction':
      default: // No Reduction
        maxReduction = 0
        monthlyReduction = 0
        ratePerThousand = isPost65 ? 2.25 : 2.5967
        break
    }
    const actualReduction = Math.min(
      monthsSinceReductionStart * monthlyReduction,
      maxReduction
    )
    reductionMultiplier = 1 - actualReduction
    const basicLifeRetirementCoverage = intakeState?.fegliExtraABL
      ? 0
      : (Math.round(fegliLookupAgeValue) < 45
          ? 1 + (45 - Math.max(Math.round(fegliLookupAgeValue), 35)) / 10
          : 1) *
        ((Math.ceil(intakeState?.baseSalary / 1000) + 2) * 1000) *
        reductionMultiplier
    const basicLife = intakeState?.fegli
      ? intakeState?.agency === 'u.s._postal_service_(usps)'
        ? {
            premium: 0,
            coverage: basicLifeCoverage,
            retirementPremium:
              ratePerThousand * (Math.ceil(intakeState?.baseSalary / 1000) + 2),
            retirementCoverage: basicLifeRetirementCoverage,
          }
        : {
            premium: 0.3467 * (Math.ceil(intakeState?.baseSalary / 1000) + 2),
            coverage: basicLifeCoverage,
            retirementPremium:
              ratePerThousand * (Math.ceil(intakeState?.baseSalary / 1000) + 2),
            retirementCoverage: basicLifeRetirementCoverage,
          }
      : ''

    // Option A reduction logic
    const cappedReductionMonthsOptionA = Math.min(
      monthsSinceReductionStart,
      37.5
    )
    const optionAreductionPercent = Math.min(
      cappedReductionMonthsOptionA * 0.02,
      0.75
    )
    // Option A Amounts
    const optionA = intakeState?.fegliOptionA
      ? {
          premium:
            selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionA,
          coverage: 10000,
          retirementPremium:
            intakeState?.fegliExtraAReduction === 'keep' && isPost65
              ? 0
              : intakeState?.fegliExtraAReduction === 'keep'
              ? selectData.fegli[fegliLookupAgeValue].optionA
              : 0,
          retirementCoverage:
            intakeState?.fegliExtraAReduction === 'keep'
              ? 10000 * (1 - optionAreductionPercent)
              : 0,
        }
      : ''

    //Option B coverage with reductions
    const optionBCoverage =
      intakeState?.fegliOptionBMultiples &&
      intakeState.fegliOptionBMultiples *
        (Math.ceil(intakeState?.baseSalary / 1000) * 1000)
    const optionBReduction = intakeState?.fegliExtraBReduction || 'none'
    const cappedReductionMonths = Math.min(monthsSinceReductionStart, 50)
    const optionBreductionPercent =
      optionBReduction === 'full' ? cappedReductionMonths * 0.02 : 0
    const optionBcoverageMultiplier =
      optionBReduction === 'full' ? 1 - optionBreductionPercent : 1
    const optionBRetirementCoverage = intakeState?.fegliExtraBKeep
      ? intakeState.fegliExtraBKeep *
        (Math.ceil(intakeState?.baseSalary / 1000) * 1000) *
        optionBcoverageMultiplier
      : 0
    // Option B Amounts
    const optionB = intakeState?.fegliOptionB
      ? {
          premium:
            selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionB *
            (optionBCoverage / 1000),
          coverage: optionBCoverage,
          retirementPremium:
            optionBReduction === 'full' && isPost65
              ? 0
              : selectData.fegli[fegliLookupAgeValue].optionB *
                (optionBRetirementCoverage / 1000),
          retirementCoverage: optionBRetirementCoverage,
        }
      : ''
    // Option C While Working Coverage and Multiples
    const childCount = Array.isArray(intakeState?.familyDependents)
      ? intakeState.familyDependents.filter((dep) => Number(dep.age) < 22)
          .length
      : intakeState?.familyChildren || 0
    const spouseAmount =
      intakeState?.familyMaritalStatus === 'married' ? 5000 : 0
    const childAmount = childCount * 2500
    const optionCMultiples = intakeState?.fegliOptionCMultiples || 0
    const optionCCoverage = optionCMultiples * (childAmount + spouseAmount)
    // Option C Retirement Coverage Calc with Children and Multiples
    const retirementChildCount = Array.isArray(intakeState?.familyDependents)
      ? intakeState.familyDependents.filter((dep) => {
          const futureAge = Number(dep.age) + yearsUntilRetirement
          return futureAge < 22
        }).length
      : intakeState?.familyChildren || 0
    const retirementSpouseAmount =
      intakeState?.familyMaritalStatus === 'married' ? 5000 : 0
    const retirementMultiples = intakeState?.fegliExtraCKeep || 0
    const optionCReduction = intakeState?.fegliExtraCReduction || 'none'
    const optionCreductionPercent =
      optionCReduction === 'full' ? cappedReductionMonths * 0.02 : 0
    const optionCcoverageMultiplier =
      optionCReduction === 'full' ? 1 - optionCreductionPercent : 1
    const baseRetirementCoverage =
      retirementMultiples *
      (retirementChildCount * 2500 + retirementSpouseAmount) *
      optionCcoverageMultiplier
    // Option C Amounts
    const optionC = intakeState?.fegliOptionC
      ? {
          premium:
            selectData.fegli[Math.floor(personalDetailsCurrentAge)].optionC *
            intakeState?.fegliOptionCMultiples,
          coverage: optionCCoverage,
          retirementPremium:
            optionCReduction === 'full' && isPost65
              ? 0
              : selectData.fegli[fegliLookupAgeValue].optionC *
                (intakeState?.fegliExtraCKeep || 0),
          retirementCoverage: baseRetirementCoverage,
        }
      : ''
    const fegliTotal = {
      premium:
        0 +
        (basicLife ? basicLife.premium : 0) +
        (optionA ? optionA.premium : 0) +
        (optionB ? optionB.premium : 0) +
        (optionC ? optionC.premium : 0),
      coverage:
        (bedb ? bedb : 0) +
        (basicLife ? basicLifeCoverage : 0) +
        (optionA ? optionA.coverage : 0) +
        (optionB ? optionB.coverage : 0),
      retirementPremium:
        0 +
        (basicLife ? basicLife.retirementPremium : 0) +
        (optionA ? optionA.retirementPremium : 0) +
        (optionB ? optionB.retirementPremium : 0) +
        (optionC ? optionC.retirementPremium : 0),
      retirementCoverage:
        0 +
        (basicLife ? basicLife.retirementCoverage : 0) +
        (optionA ? optionA.retirementCoverage : 0) +
        (optionB ? optionB.retirementCoverage : 0) +
        (optionC ? optionC.retirementCoverage : 0),
    }

    const privateInsurance = intakeState?.privateLifeInsurance || []
    const totalPrivateCoverage = privateInsurance.reduce(
      (sum, policy) => sum + (policy.coverage || 0),
      0
    )
    const totalPrivatePremium = privateInsurance.reduce(
      (sum, policy) => sum + (policy.premium || 0),
      0
    )

    // const privateInsurance = intakeState?.lifeInsuranceOther
    //   ? {
    //       provider: !intakeState?.lifeInsuranceOtherProvider
    //         ? 'Not Selected'
    //         : selectData.privateLifeInsuranceProviders.find(
    //             ({ value }) => value === intakeState?.lifeInsuranceOtherProvider
    //           ).label,
    //       logo: !intakeState?.lifeInsuranceOtherProvider
    //         ? ''
    //         : selectData.privateLifeInsuranceProviders.find(
    //             ({ value }) => value === intakeState?.lifeInsuranceOtherProvider
    //           ).logo,
    //       coverage: !intakeState?.lifeInsuranceOtherCoverageAmount
    //         ? 0
    //         : intakeState.lifeInsuranceOtherCoverageAmount,
    //       premium: !intakeState?.lifeInsuranceOtherPremium
    //         ? 0
    //         : intakeState.lifeInsuranceOtherPremium,
    //       type: !intakeState?.lifeInsuranceOtherType
    //         ? ''
    //         : selectData.privateLifeInsuranceTypes.find(
    //             ({ value }) => value === intakeState?.lifeInsuranceOtherType
    //           ).label,
    //     }
    //   : ''

    const allLifeInsuranceTotal = {
      fegliPremium: isNaN(fegliTotal.premium) ? 0 : fegliTotal.premium,
      fegliCoverage: isNaN(fegliTotal.coverage) ? 0 : fegliTotal.coverage,
      privatePremium: isNaN(totalPrivatePremium) ? 0 : totalPrivatePremium,
      privateCoverage: isNaN(totalPrivateCoverage) ? 0 : totalPrivateCoverage,
      premium:
        (isNaN(fegliTotal.premium) ? 0 : fegliTotal.premium) +
        (isNaN(totalPrivatePremium) ? 0 : totalPrivatePremium),
      coverage:
        (isNaN(fegliTotal.coverage) ? 0 : fegliTotal.coverage) +
        (isNaN(totalPrivateCoverage) ? 0 : totalPrivateCoverage),
    }

    const annualLeave =
      (intakeState?.baseSalary / 2087) * intakeState.currentLeaveBalance
    const annualLeaveNet = annualLeave * (1 - 0.3145)
    const survivorBenefitValue =
      intakeState.familyMaritalStatus === 'single'
        ? 0
        : presentValueSurvivorBenefit
    const totalEstate =
      allLifeInsuranceTotal.coverage + annualLeaveNet + survivorBenefitValue

    // Need to know child ages to take into account
    // add in credit cards and mortgages
    // or do we need to separate this from the net worth?
    const grossCoverageNeeded = presentValueClientIncome

    return {
      plannedRetirementMonth,
      plannedRetirementDay,
      plannedRetirementYear,
      bedb,
      basicLife,
      optionA,
      optionB,
      optionC,
      fegliTotal,
      privateInsurance,
      allLifeInsuranceTotal,
      annualLeave,
      annualLeaveNet,
      survivorBenefitValue,
      survivorBenefitCost,
      totalEstate,
      grossCoverageNeeded,
      getRankedFEGLICombos,
    }
  } catch (e) {
    return {}
  }
}
